import { all, call, put, select, takeEvery } from "redux-saga/effects";
import api from "../../utils/api";
import { errorAlert, successAlert } from "../Alerts/actions";
import {
  fetchKitchenDishes,
  fetchKitchenDishesSuccess,
  fetchKitchenDishesFailure,
  fetchKitchenMenus,
  fetchKitchenMenusSuccess,
  fetchKitchenMenusFailure,
  fetchMealSittingsSuccess,
  fetchMealSittingsFailure,
} from "./actions";
import {
  FETCH_KITCHEN_DISHES,
  SAVE_KITCHEN_DISH,
  DELETE_KITCHEN_DISH,
  FETCH_KITCHEN_MENUS,
  SAVE_KITCHEN_MENU,
  PUBLISH_KITCHEN_MENU,
  UNPUBLISH_KITCHEN_MENU,
  FETCH_KITCHEN_DISH,
  FETCH_DISH_PHOTO_LIBRARY,
  UPLOAD_DISH_PHOTO,
  ATTACH_DISH_PHOTO,
  UPDATE_DISH_PHOTO,
  DELETE_DISH_PHOTO,
  POLISH_DISH_PHOTO,
  TIDY_DISH_DESCRIPTION,
  FETCH_MEAL_SITTINGS,
  SAVE_MEAL_SITTINGS,
} from "./constants";
import {
  normaliseSittings,
  serialiseSittings,
} from "../../Containers/Kitchen/mealSittings";

const unwrap = (data) => data?.data ?? data ?? {};

export function* fetchKitchenDishesSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { id } = payload || {};
  try {
    const { data } = yield call(
      api(token).get,
      `/api/location/${id}/dishes?include_inactive=1`,
    );
    const body = unwrap(data);
    yield put(
      fetchKitchenDishesSuccess({
        dishes: Array.isArray(body) ? body : (body.dishes ?? []),
      }),
    );
  } catch (e) {
    yield put(fetchKitchenDishesFailure());
    yield put(errorAlert("Error while loading dishes."));
  }
}

export function* saveKitchenDishSaga({ payload, onDone }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { locationId, dishId, values } = payload || {};
  try {
    if (dishId) {
      yield call(
        api(token).put,
        `/api/location/${locationId}/dishes/${dishId}`,
        values,
      );
    } else {
      yield call(api(token).post, `/api/location/${locationId}/dishes`, values);
    }
    yield put(successAlert(dishId ? "Dish updated." : "Dish added."));
    yield put(fetchKitchenDishes({ id: locationId }));
    typeof onDone === "function" && onDone();
  } catch (e) {
    yield put(errorAlert("Could not save the dish."));
    yield put(fetchKitchenDishesFailure());
  }
}

export function* deleteKitchenDishSaga({ payload, onDone }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { locationId, dishId } = payload || {};
  try {
    yield call(
      api(token).delete,
      `/api/location/${locationId}/dishes/${dishId}`,
    );
    yield put(successAlert("Dish removed."));
    yield put(fetchKitchenDishes({ id: locationId }));
    typeof onDone === "function" && onDone();
  } catch (e) {
    yield put(errorAlert("Could not delete the dish."));
    yield put(fetchKitchenDishesFailure());
  }
}

export function* fetchKitchenMenusSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { id, from, to } = payload || {};
  const query = `?from=${from}&to=${to}`;
  try {
    const { data } = yield call(
      api(token).get,
      `/api/location/${id}/menus${query}`,
    );
    const body = unwrap(data);
    yield put(
      fetchKitchenMenusSuccess({
        menus: body.menus ?? [],
        from: body.from ?? from,
        to: body.to ?? to,
      }),
    );
  } catch (e) {
    yield put(fetchKitchenMenusFailure());
    yield put(errorAlert("Error while loading menus."));
  }
}

export function* saveKitchenMenuSaga({ payload, onDone }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { locationId, values, from, to } = payload || {};
  try {
    const { data } = yield call(
      api(token).put,
      `/api/location/${locationId}/menus`,
      values,
    );
    const body = unwrap(data);
    yield put(successAlert("Menu saved."));
    yield put(fetchKitchenMenus({ id: locationId, from, to }));
    typeof onDone === "function" && onDone(body);
  } catch (e) {
    yield put(fetchKitchenMenusFailure());
    yield put(errorAlert("Could not save the menu."));
  }
}

export function* publishKitchenMenuSaga({ payload, onDone }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { locationId, menuId, from, to } = payload || {};
  try {
    yield call(
      api(token).post,
      `/api/location/${locationId}/menus/${menuId}/publish`,
    );
    yield put(successAlert("Menu published."));
    yield put(fetchKitchenMenus({ id: locationId, from, to }));
    typeof onDone === "function" && onDone();
  } catch (e) {
    yield put(fetchKitchenMenusFailure());
    yield put(errorAlert("Could not publish the menu. Add at least one dish."));
  }
}

export function* unpublishKitchenMenuSaga({ payload, onDone }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { locationId, menuId, from, to } = payload || {};
  try {
    yield call(
      api(token).post,
      `/api/location/${locationId}/menus/${menuId}/unpublish`,
    );
    yield put(successAlert("Menu moved back to draft."));
    yield put(fetchKitchenMenus({ id: locationId, from, to }));
    typeof onDone === "function" && onDone();
  } catch (e) {
    yield put(fetchKitchenMenusFailure());
    yield put(errorAlert("Could not unpublish the menu."));
  }
}

/* ---- Dish photos + ✦ Tidy (wave 2 contract §A3, 29 Sep 2026) ----
   The API is built in parallel: every endpoint may still 404 on an older
   server. A 404/405 is never toasted — the editor hides the Photos panel with
   a quiet note instead. */

// The interceptor already returns the API envelope; tolerate a bare payload too.
const bodyOf = (res) => unwrap(res?.data ?? res);

const errorInfo = (e, fallback) => {
  const status = e?.response?.status ?? null;
  const message =
    (typeof e === "string" && e) ||
    (typeof e?.error === "string" && e.error) ||
    fallback;
  return {
    status,
    notFound: status === 404 || status === 405,
    forbidden: status === 403,
    message,
  };
};

const done = (fn, value) => typeof fn === "function" && fn(value);

const photoOf = (body) =>
  body && typeof body === "object" && body.photo ? body.photo : body;

export function* fetchKitchenDishSaga({ payload, onSuccess, onError }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { locationId, dishId } = payload || {};
  try {
    const res = yield call(
      api(token).get,
      `/api/location/${locationId}/dishes/${dishId}`,
    );
    const body = bodyOf(res);
    done(onSuccess, body?.dish ?? body);
  } catch (e) {
    done(onError, errorInfo(e, "Could not load the dish."));
  }
}

export function* fetchDishPhotoLibrarySaga({ payload, onSuccess, onError }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { locationId, search = "", dishId, silent = false } = payload || {};
  const params = new URLSearchParams();
  if (search) params.set("search", search);
  if (dishId) params.set("dish_id", dishId);
  const query = params.toString();
  try {
    const res = yield call(
      api(token).get,
      `/api/location/${locationId}/dish-photos${query ? `?${query}` : ""}`,
    );
    const body = bodyOf(res);
    const photos = Array.isArray(body)
      ? body
      : Array.isArray(body?.photos)
        ? body.photos
        : Array.isArray(body?.photos?.data)
          ? body.photos.data
          : Array.isArray(body?.data)
            ? body.data
            : [];
    done(
      onSuccess,
      photos.filter((p) => p && p.url),
    );
  } catch (e) {
    const info = errorInfo(e, "Could not load the photo library.");
    if (!info.notFound && !silent) yield put(errorAlert(info.message));
    done(onError, info);
  }
}

export function* uploadDishPhotoSaga({ payload, onSuccess, onError }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { dishId, file, iddsiLevel = null, source = "upload" } = payload || {};
  try {
    const fd = new FormData();
    fd.append("photo", file);
    fd.append("source", source);
    if (iddsiLevel !== null && iddsiLevel !== undefined) {
      fd.append("iddsi_level", String(iddsiLevel));
    }
    const res = yield call(api(token).post, `/api/dish/${dishId}/photos`, fd, {
      timeout: 120000,
    });
    yield put(successAlert("Photo added."));
    done(onSuccess, photoOf(bodyOf(res)));
  } catch (e) {
    const info = errorInfo(e, "Could not upload the photo.");
    if (!info.notFound) {
      yield put(
        errorAlert(
          info.forbidden
            ? "Ask the kitchen or a manager to add photos."
            : info.message,
        ),
      );
    }
    done(onError, info);
  }
}

export function* attachDishPhotoSaga({ payload, onSuccess, onError }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { dishId, photoId, iddsiLevel = null } = payload || {};
  try {
    // Main photo = no iddsi_level at all (never send null to a validator).
    const body = { photo_uuid: photoId };
    if (iddsiLevel !== null && iddsiLevel !== undefined) {
      body.iddsi_level = iddsiLevel;
    }
    const res = yield call(
      api(token).post,
      `/api/dish/${dishId}/photos/attach`,
      body,
    );
    yield put(successAlert("Photo added from the library."));
    done(onSuccess, photoOf(bodyOf(res)));
  } catch (e) {
    const info = errorInfo(e, "Could not reuse that photo.");
    if (!info.notFound) yield put(errorAlert(info.message));
    done(onError, info);
  }
}

export function* updateDishPhotoSaga({ payload, onSuccess, onError }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { photoId, values, silent = false } = payload || {};
  try {
    const res = yield call(
      api(token).patch,
      `/api/dish-photo/${photoId}`,
      values || {},
    );
    if (!silent) yield put(successAlert("Main photo updated."));
    done(onSuccess, photoOf(bodyOf(res)));
  } catch (e) {
    const info = errorInfo(e, "Could not update the photo.");
    if (!info.notFound) yield put(errorAlert(info.message));
    done(onError, info);
  }
}

export function* deleteDishPhotoSaga({ payload, onSuccess, onError }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { photoId, silent = false } = payload || {};
  try {
    yield call(api(token).delete, `/api/dish-photo/${photoId}`);
    if (!silent) yield put(successAlert("Photo removed."));
    done(onSuccess, photoId);
  } catch (e) {
    const info = errorInfo(e, "Could not remove the photo.");
    if (!info.notFound && !silent) yield put(errorAlert(info.message));
    done(onError, info);
  }
}

/** Soft-fails on the API: `reason: 'unavailable' | 'failed'` arrives as 200. */
export function* polishDishPhotoSaga({ payload, onSuccess, onError }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { photoId } = payload || {};
  try {
    const res = yield call(
      api(token).post,
      `/api/dish-photo/${photoId}/polish`,
      {},
      { timeout: 300000 },
    );
    const body = bodyOf(res);
    done(onSuccess, {
      original: body?.original ?? null,
      polished: body?.polished ?? null,
      reason: body?.reason ?? (body?.polished ? null : "failed"),
    });
  } catch (e) {
    done(onError, errorInfo(e, "Couldn't polish this photo."));
  }
}

/** Errors stay inline under the Description field — no toast. */
export function* tidyDishDescriptionSaga({ payload, onSuccess, onError }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { name, description } = payload || {};
  try {
    const res = yield call(
      api(token).post,
      `/api/dish/tidy-description`,
      { name, description },
      { timeout: 300000 },
    );
    const body = bodyOf(res);
    done(onSuccess, {
      suggestion:
        typeof body?.suggestion === "string" ? body.suggestion.trim() : "",
      reason: body?.reason ?? null,
    });
  } catch (e) {
    done(onError, errorInfo(e, "Couldn't tidy that right now."));
  }
}

/* ---- Meal times per home (wave 3 contract §2/§4, 30 Sep 2026) ----
   GET/PUT location/{id}/meal-sittings. Built alongside the API: a 404/405 is an
   older server (every screen keeps today's standard times), a 503 is the held
   column not yet added. Neither is toasted — the Meal times page says it inline.
   The shared fetch is quiet too: a kitchen board never toasts over its tabs. */

const pendingSittings = new Set();

const sittingsStatus = (info) => {
  if (info.notFound) return "missing";
  if (info.status === 503) return "needs_update";
  if (info.forbidden) return "forbidden";
  return "error";
};

/** Laravel 422 `errors` ({ "sittings.2.from": ["…"] }) → { 2: { from: "…" } }. */
const sittingFieldErrors = (e) => {
  // Laravel's `errors`, or this API's `error` object (validatorErrorResponse:
  // { status: "failed", error: { "sittings.2.grace_min": "…" } }).
  const data = e?.response?.data || {};
  const errors =
    data.errors ||
    (data.error && typeof data.error === "object" ? data.error : null);
  if (!errors || typeof errors !== "object") return null;
  const out = {};
  Object.entries(errors).forEach(([path, messages]) => {
    const m = /^sittings\.(\d+)\.(\w+)/.exec(path);
    const text = Array.isArray(messages) ? messages[0] : messages;
    if (!m || typeof text !== "string") return;
    const index = Number(m[1]);
    out[index] = out[index] || {};
    if (!out[index][m[2]]) out[index][m[2]] = text;
  });
  return Object.keys(out).length ? out : null;
};

const sittingsInfo = (e, fallback) => {
  const info = errorInfo(e, fallback);
  return {
    ...info,
    needsUpdate: info.status === 503,
    fieldErrors: info.status === 422 ? sittingFieldErrors(e) : null,
  };
};

const readSittingsBody = (body) => {
  const list = Array.isArray(body?.sittings)
    ? body.sittings
    : Array.isArray(body?.meal_sittings)
      ? body.meal_sittings
      : Array.isArray(body)
        ? body
        : null;
  return {
    sittings: normaliseSittings(list),
    isDefault:
      typeof body?.is_default === "boolean"
        ? body.is_default
        : !(list && list.length),
    canEdit: typeof body?.can_edit === "boolean" ? body.can_edit : null,
  };
};

export function* fetchMealSittingsSaga({ payload, onSuccess, onError }) {
  const { locationId, force = false } = payload || {};
  if (!locationId) return;
  if (pendingSittings.has(locationId) && !force) return;
  pendingSittings.add(locationId);
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const res = yield call(
      api(token).get,
      `/api/location/${locationId}/meal-sittings`,
    );
    const result = readSittingsBody(bodyOf(res));
    yield put(fetchMealSittingsSuccess({ locationId, ...result }));
    done(onSuccess, result);
  } catch (e) {
    const info = sittingsInfo(e, "Could not load meal times.");
    yield put(
      fetchMealSittingsFailure({ locationId, status: sittingsStatus(info) }),
    );
    done(onError, info);
  } finally {
    pendingSittings.delete(locationId);
  }
}

export function* saveMealSittingsSaga({ payload, onSuccess, onError }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { locationId, sittings } = payload || {};
  const sent = serialiseSittings(sittings);
  try {
    const res = yield call(
      api(token).put,
      `/api/location/${locationId}/meal-sittings`,
      { sittings: sent },
    );
    const body = bodyOf(res);
    const result = Array.isArray(body?.sittings)
      ? readSittingsBody(body)
      : { sittings: normaliseSittings(sent), isDefault: false, canEdit: true };
    yield put(fetchMealSittingsSuccess({ locationId, ...result }));
    yield put(successAlert("Meal times saved."));
    done(onSuccess, result);
  } catch (e) {
    const info = sittingsInfo(e, "Could not save meal times.");
    if (
      !info.notFound &&
      !info.forbidden &&
      !info.needsUpdate &&
      info.status !== 422
    ) {
      yield put(errorAlert(info.message));
    }
    done(onError, info);
  }
}

export default function* KitchenMenuSaga() {
  yield all([
    takeEvery(FETCH_MEAL_SITTINGS, fetchMealSittingsSaga),
    takeEvery(SAVE_MEAL_SITTINGS, saveMealSittingsSaga),
    takeEvery(FETCH_KITCHEN_DISH, fetchKitchenDishSaga),
    takeEvery(FETCH_DISH_PHOTO_LIBRARY, fetchDishPhotoLibrarySaga),
    takeEvery(UPLOAD_DISH_PHOTO, uploadDishPhotoSaga),
    takeEvery(ATTACH_DISH_PHOTO, attachDishPhotoSaga),
    takeEvery(UPDATE_DISH_PHOTO, updateDishPhotoSaga),
    takeEvery(DELETE_DISH_PHOTO, deleteDishPhotoSaga),
    takeEvery(POLISH_DISH_PHOTO, polishDishPhotoSaga),
    takeEvery(TIDY_DISH_DESCRIPTION, tidyDishDescriptionSaga),
    takeEvery(FETCH_KITCHEN_DISHES, fetchKitchenDishesSaga),
    takeEvery(SAVE_KITCHEN_DISH, saveKitchenDishSaga),
    takeEvery(DELETE_KITCHEN_DISH, deleteKitchenDishSaga),
    takeEvery(FETCH_KITCHEN_MENUS, fetchKitchenMenusSaga),
    takeEvery(SAVE_KITCHEN_MENU, saveKitchenMenuSaga),
    takeEvery(PUBLISH_KITCHEN_MENU, publishKitchenMenuSaga),
    takeEvery(UNPUBLISH_KITCHEN_MENU, unpublishKitchenMenuSaga),
  ]);
}
